import axios from 'axios'
import Cookies from 'universal-cookie'
// LAP-733 (GE022) — read the user's chosen UI language so the backend's
// LanguageMiddleware can localise responses (Lifeapp.Nom, etc.).
import i18n, { SUPPORTED_LANGUAGES } from '../i18n'

export function getUrl() {
  return process.env.REACT_APP_LIFE_BACKEND_URL
    ? process.env.REACT_APP_LIFE_BACKEND_URL
    : 'http://192.168.8.100:8000'
}

// Axios default configuation
// axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
// axios.defaults.headers.common['Access-Control-Allow-Methods'] =
//   'GET, POST, PUT, DELETE, PATCH, OPTIONS';
// axios.defaults.headers.common['Access-Control-Allow-Headers'] =
//   'DNT,User-Agent,X-Requested-With,If-Modified-Since,Cache-Control,Content-Type,Range';
axios.defaults.headers.common['Content-Type'] = 'application/json'
axios.defaults.headers.common.Accept = 'application/json'

// LAP-733 (GE022) — every API request advertises the user's chosen UI
// language. The backend's `LanguageMiddleware` reads `Accept-Language` (and
// `?language=` for explicit overrides) to localise responses (Lifeapp names,
// publications, …). Without this, switching the UI language wouldn't change
// the strings that come from the database.
function getActiveLanguage() {
  // i18n.language can be 'fr-FR', 'de', etc. We only want the primary tag.
  const raw = (i18n && i18n.language) || ''
  const primary = String(raw).split('-')[0].split('_')[0].toLowerCase()
  return SUPPORTED_LANGUAGES.includes(primary) ? primary : 'fr'
}

axios.interceptors.request.use((config) => {
  try {
    const lang = getActiveLanguage()
    config.headers = config.headers || {}
    // Only set if the caller hasn't already provided one (rare).
    if (!config.headers['Accept-Language'] && !config.headers['accept-language']) {
      config.headers['Accept-Language'] = lang
    }
    // Belt-and-braces: also append ?language=xx so resolution still works
    // even if a CDN/proxy strips the Accept-Language header on the way in.
    if (config.url && !/[?&]language=/.test(config.url)) {
      const sep = config.url.includes('?') ? '&' : '?'
      config.url = `${config.url}${sep}language=${lang}`
    }
  } catch (_) {
    // Never block a request because of language injection.
  }
  return config
})

// Sans timeout, axios attend INDÉFINIMENT si le backend ne répond pas
// (serveur arrêté, mauvais port, réseau coupé...). Conséquence visible :
// le LoadingButton de la page de connexion tourne dans le vide pour
// toujours et le bloc finally qui remet submitting=false ne s'exécute
// jamais. 20s est un compromis large pour un endpoint d'auth.
axios.defaults.timeout = 20000

/**
 * Routes d'authentification publiques pour lesquelles un 401 est une réponse
 * métier légitime (mauvais mot de passe, email inconnu...) et NON une session
 * expirée. L'intercepteur global ne doit pas rediriger vers /login pour ces
 * routes — sinon on crée une boucle de rechargement et un faux toast
 * "Session expirée".
 */
const AUTH_PUBLIC_ROUTES = [
  '/api/user/login/',
  '/api/user/register/',
  '/api/user/verify-mail/',
  '/api/user/verify-mail-password/',
  '/api/user/reset-password/',
  '/api/user/mail-code/',
]

function isAuthPublicRoute(url = '') {
  return AUTH_PUBLIC_ROUTES.some((route) => url.includes(route))
}

// Petit bus d'événements : le slice billing s'abonne à 'life:insufficient-credits'
// et ouvre la modale. Découple l'intercepteur du store Redux (pas d'import
// circulaire entre utils/api.js et reduxLib/store.js).
function emitInsufficientCredits(payload) {
  try {
    window.dispatchEvent(
      new CustomEvent('life:insufficient-credits', { detail: payload || null }),
    )
  } catch (_) {
    // Cas SSR / environnements sans CustomEvent — ignoré silencieusement.
  }
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || ''
    const isPublicAuth = isAuthPublicRoute(requestUrl)
    // Règle de sûreté : sans token, il ne peut PAS y avoir de session expirée.
    // Cette garde évite la boucle classique "401 sur /login → redirect /login →
    // reload → resubmit → 401…" même si le filtre par URL échoue (HMR, cache,
    // URL encodée différemment, etc.).
    const hasSession = !!localStorage.getItem('token')

    // HTTP 402 = Payment Required. Renvoyé par le décorateur
    // `requires_credits` côté backend quand le wallet est insuffisant ou
    // quand le tier IA n'est pas autorisé sur le plan courant.
    // Le payload (cf. CreditService.InsufficientCreditsError.to_payload) :
    //   { code, required, available, missing, feature, tier }
    if (error.response?.status === 402) {
      emitInsufficientCredits(error.response?.data)
      // On laisse l'erreur remonter pour que le composant appelant puisse
      // aussi annuler son loader / nettoyer son état local.
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      // On ne déclenche le scénario "session expirée" QUE si l'utilisateur
      // avait effectivement une session ET que la route n'est pas une route
      // d'auth publique. Sinon, on laisse le composant traiter l'erreur.
      if (hasSession && !isPublicAuth) {
        localStorage.setItem('login-again', true)
        localStorage.removeItem('token')
        window.location.href = '/login'
      }
    } else if (error.response?.status === 403) {
      // Idem pour 403 : route d'auth publique OU pas de session = cas métier
      // (email non vérifié, compte désactivé…) géré par le composant appelant.
      if (isPublicAuth || !hasSession) {
        return Promise.reject(error)
      }
      // Handle Access Denied: set message so AccessDenied shows it immediately (no flash)
      const msg = error.response?.data?.message || error.response?.data?.detail || "Cette fonctionnalité n'est pas disponible avec votre abonnement."
      try {
        localStorage.setItem('denied_message', typeof msg === 'string' ? msg : JSON.stringify(msg))
      } catch (_) {}
      window.location.href = '/access-denied'
      return
    }

    // Synthèse pour les erreurs sans réponse HTTP (timeout, réseau, CORS).
    // Sans ça, makePostRequest renvoie `undefined` et le composant ne peut
    // pas distinguer "serveur trop lent" de "pas de réseau". On normalise
    // tout au format {status, data:{code, message}} pour parseAuthError.
    if (!error.response) {
      const isTimeout =
        error.code === 'ECONNABORTED' ||
        error.code === 'ETIMEDOUT' ||
        /timeout/i.test(error.message || '')
      error.response = {
        status: 0,
        data: {
          code: isTimeout ? 'TIMEOUT' : 'NETWORK_ERROR',
          message: isTimeout
            ? 'Le serveur met trop de temps à répondre. Vérifiez votre connexion ou réessayez.'
            : 'Connexion impossible au serveur. Vérifiez votre connexion internet.',
        },
      }
    }

    return Promise.reject(error)
  },
)

const GetAuthToken = () => {
  const token = localStorage.getItem('token')
  return { headers: { Authorization: `Token ${token}` } }
}

function makeGetRequest(url, config, withToken = false) {
  const tokenHeaders = withToken ? GetAuthToken() : null
  return axios
    .get(getUrl() + url, {
      headers: {
        ...config?.headers,
        ...tokenHeaders?.headers,
      },
    })
    .then((response) => response)
    .catch((error) => error.response)
}

function makeCustomGetRequest(url, config, withToken = false) {
  const tokenHeaders = withToken ? GetAuthToken() : null
  return axios
    .get(url, {
      headers: {
        ...config?.headers,
        ...tokenHeaders?.headers,
      },
    })
    .then((response) => response)
    .catch((error) => error.response)
}

function makePostRequest(url, bodyFormData, config, withToken = false) {
  const tokenHeaders = withToken ? GetAuthToken() : null

  return axios
    .post(getUrl() + url, bodyFormData, {
      ...config?.custom,
      headers: {
        ...config?.headers,
        ...tokenHeaders?.headers,
      },
    })
    .then((response) => response)
    .catch((error) => error.response)
}

function makePatchRequest(url, bodyFormData, config, withToken = false) {
  const tokenHeaders = withToken ? GetAuthToken() : null
  return axios
    .patch(getUrl() + url, bodyFormData, {
      headers: {
        ...config?.headers,
        ...tokenHeaders?.headers,
      },
    })
    .then((response) => response)
    .catch((error) => error.response)
}

function makePutRequest(url, bodyFormData, config, withToken = false) {
  const tokenHeaders = withToken ? GetAuthToken() : null
  return axios
    .put(getUrl() + url, bodyFormData, {
      headers: {
        ...config?.headers,
        ...tokenHeaders?.headers,
      },
    })
    .then((response) => response)
    .catch((error) => error.response)
}

function makeDeleteRequest(url, config, withToken = false) {
  const tokenHeaders = withToken ? GetAuthToken() : null
  return axios
    .delete(getUrl() + url, {
      headers: {
        ...config?.headers,
        ...tokenHeaders?.headers,
      },
    })
    .then((response) => response)
    .catch((error) => error.response)
}

function singleFileInstantUploadServer(requestUrl, fieldName, method = 'post') {
  return {
    process: (defaultFieldName, file, metadata, load, error, progress, abort) => {
      // fieldName is the name of the input field
      // file is the actual file object to send
      const { CancelToken } = axios
      let cancel
      const formData = new FormData()
      formData.append(fieldName, file)
      const cookies = new Cookies()
      const token = cookies.get('token')

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Token ${token}`,
        },

        onUploadProgress: (e) => {
          progress(e.lengthComputable, e.loaded, e.total)
        },
        cancelToken: new CancelToken((c) => {
          cancel = c
        }),
      }
      switch (method) {
        case 'patch':
          config._method = 'patch'
          axios.patch(`${getUrl()}${requestUrl}`, formData, config).then((response) => {
            load(response.data)
          })
          break
        default:
          axios.post(`${getUrl()}${requestUrl}`, formData, config)
          break
      }

      // Should expose an abort method so the request can be cancelled
      return {
        abort: () => {
          // This function is entered if the user has tapped the cancel button
          cancel()

          // Let FilePond know the request has been cancelled
          abort()
        },
      }
    },
  }
}

export {
  makeGetRequest,
  makePostRequest,
  makePatchRequest,
  makePutRequest,
  makeDeleteRequest,
  makeCustomGetRequest,
  singleFileInstantUploadServer,
  GetAuthToken,
}
